import React, { useContext, useEffect, useState } from "react";
import { Layout } from "@shopify/polaris";
import { useQuery, useLazyQuery } from "@apollo/client";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import constant
import constant from "lib/constant/constant";

// import context
import { PrivateContext } from "lib/context/privateContext";

// import helper
import { baseHelper } from "lib/helpers";

// import queries
import { GET_AVAILABILITY, GET_METAFIELD_SETTING, GET_PRODUCT } from "app/product/apollo/queries";

// import components
import Banner from "lib/components/banner/banner";
import { SkeletonAnnotated } from "lib/components";

// import tab
import { useParams } from "react-router-dom";
import EditServiceTabs from "./features/form/service/serviceTabs";

const ProductServiceEdit = () => {
  const { isLoading } = useContext(PrivateContext);
  const { id } = useParams();
  const [description, setDescription] = useState("");
  const [title, setTitle] = useState("");
  const [isVariant, setIsVariant] = useState(false);
  const [isMetafield, setIsMetafield] = useState(false);
  const [availability, setAvailability] = useState({});
  const [serviceId, setServiceId] = useState("");

  const [banner, setBanner] = useState({
    action: null,
    children: null,
    isOpen: false,
    status: "",
    title: "",
  });
  const onDismissBanner = () => {
    setBanner({
      action: null,
      children: null,
      isOpen: false,
      status: "",
      title: "",
    });
  };

  const { loading: productLoading, data: productData, refetch } = useQuery(GET_PRODUCT, {
    variables: { input: { id } },
  });

  const { loading: metafieldLoading, data: metaFieldData } = useQuery(GET_METAFIELD_SETTING);

  const [getAvailability, { loading: availabilityLoading, data: availabilityData }] = useLazyQuery(GET_AVAILABILITY);

  useEffect(() => {
    if (metaFieldData) {
      const metafieldSetting = baseHelper.getResponseData(metaFieldData, constant.gql.GET_METAFIELD_SETTING) || {};
      const { metafield = [] } = metafieldSetting;

      if (metafield && metafield.length) {
        setIsMetafield(true);
        return;
      }
      setIsMetafield(false);
    }
  }, [metaFieldData]);

  useEffect(() => {
    if (productData) {
      const productResponse = baseHelper.getResponseData(productData, constant.gql.GET_PRODUCT) || {};
      const { variants = [], serviceId: productServiceId } = productResponse || {};

      if (productServiceId) {
        setServiceId(productServiceId);
        getAvailability({ variables: { input: { id: productServiceId } } });
      }

      if (variants && variants.length) {
        setIsVariant(true);
        return;
      }
      setIsVariant(false);
    }
  }, [productData]);

  useEffect(() => {
    if (availabilityData) {
      const productResponse = baseHelper.getResponseData(availabilityData, constant.gql.GET_SERVICE) || {};
      if (productResponse) {
        setAvailability(productResponse);
      }
    }
  }, [availabilityData]);

  if (isLoading || productLoading || metafieldLoading || availabilityLoading) {
    return <SkeletonAnnotated />;
  }

  return (
    <>
      {banner.isOpen && (
        <Layout.Section>
          <Banner
            title={banner.title}
            status={banner.status}
            isOpen={banner.isOpen}
            action={banner.action}
            onDismiss={onDismissBanner}
          >
            {banner.children}
          </Banner>
        </Layout.Section>
      )}
      <br />
      <Layout.AnnotatedSection title={title} description={description}>
        <EditServiceTabs
          setBanner={setBanner}
          setDescription={setDescription}
          setTitle={setTitle}
          isVariant={isVariant}
          refetch={refetch}
          setIsVariant={setIsVariant}
          isMetafield={isMetafield}
          availability={availability}
          serviceId={serviceId}
          getAvailability={getAvailability}
        />
      </Layout.AnnotatedSection>
    </>
  );
};

export default withFeature(withErrorBoundary(ProductServiceEdit), { feature: constant.PRODUCT_UPDATE });
